import { useState, useEffect } from "react";
import { FolderKanban, Plus, Edit2, Trash2, Loader2, Sparkles, Cpu } from "lucide-react";
import { fetchPortfolios, createPortfolio, updatePortfolio, deletePortfolio } from "../../lib/api";

const initialForm = {
  title: "",
  slug: "",
  category: "",
  client: "",
  short_description: "",
  long_description: "",
  technologies: "", // Controlled string tracking 
  project_url: "",
  image_url: "",
  display_order: 0,
  is_featured: false,
  is_active: true,
};

export default function ManagePortfolios() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadPortfolios();
  }, []);

  async function loadPortfolios() {
    try {
      setLoading(true);
      const data = await fetchPortfolios();
      if (data.success) setItems(data.portfolios || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setForm(prev => ({
      ...prev,
      title: val,
      slug: editingId ? prev.slug : val.toLowerCase().trim().replace(/[^a-z0-9-_ ]/g, "").replace(/\s+/g, "-")
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleEditInit = (item) => {
    setEditingId(item.id);
    setError(null);
    setSuccess(null);
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      category: item.category || "",
      client: item.client || "",
      short_description: item.short_description || "",
      long_description: item.long_description || "",
      technologies: item.technologies || "", // Directly load comma-separated row text
      project_url: item.project_url || "",
      image_url: item.image_url || "",
      display_order: item.display_order || 0,
      is_featured: Boolean(item.is_featured),
      is_active: Boolean(item.is_active),
    });
  };

  const handleClearForm = () => {
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingId) {
        await updatePortfolio(editingId, form);
        setSuccess("Portfolio catalog structure mutated successfully.");
      } else {
        await createPortfolio(form);
        setSuccess("New enterprise showcase instance loaded cleanly.");
      }
      handleClearForm();
      await loadPortfolios();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you absolute sure you want to drop this system catalog node?")) return;
    try {
      await deletePortfolio(id);
      setSuccess("Portfolio resource row purged cleanly.");
      if (editingId === id) handleClearForm();
      await loadPortfolios();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 lg:p-8 min-h-screen text-foreground">
      <div className="flex items-center justify-between border-b border-border/60 pb-5 mb-8">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand">
            <FolderKanban className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Case Studies Matrix</h1>
            <p className="text-xs text-muted-foreground">Provision enterprise work portfolios and consumer experience systems live.</p>
          </div>
        </div>
      </div>

      {error && <div className="mb-6 rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive font-medium animate-fade-in">{error}</div>}
      {success && <div className="mb-6 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-400 font-medium animate-fade-in">{success}</div>}

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
        {/* Left Hand: Collection Node List */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Live Repositories</h2>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center p-12 text-muted-foreground border border-dashed border-border rounded-2xl bg-card/40">
              <Loader2 className="h-6 w-6 animate-spin text-brand mb-2" />
              <p className="text-xs">Parsing core schemas...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center p-12 text-xs text-muted-foreground border border-dashed border-border rounded-2xl">
              No project architectures recorded within target schemas. Use form workspace.
            </div>
          ) : (
            <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-1">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className={`group p-4 rounded-xl border transition-all flex items-center justify-between gap-4 bg-card hover:border-brand/40 ${
                    editingId === item.id ? "border-brand shadow-brand-glow bg-brand/5" : "border-border/80"
                  }`}
                >
                  <div className="flex items-center gap-4 overflow-hidden">
                    <div className="h-12 w-12 rounded-lg bg-secondary overflow-hidden shrink-0 border border-border">
                      {item.image_url ? (
                        <img src={item.image_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full grid place-items-center text-muted-foreground text-[10px]">Empty</div>
                      )}
                    </div>
                    <div className="overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm truncate">{item.title}</h4>
                        {Boolean(item.is_featured) && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-sun/10 text-sun border border-sun/20 font-medium flex items-center gap-1">
                            <Sparkles className="h-2 w-2" /> Featured
                          </span>
                        )}
                        {!Boolean(item.is_active) && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground border">Draft</span>
                        )}
                      </div>
                      <p className="text-xs text-brand/80 font-medium mt-0.5">{item.category}</p>
                      
                      {/* Tech Badges inside Admin list */}
                      {item.technologies && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {item.technologies.split(",").map((tech, i) => (
                            <span key={i} className="text-[9px] px-1 bg-secondary border text-muted-foreground rounded">
                              {tech.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100 transition">
                    <button 
                      type="button" onClick={() => handleEditInit(item)}
                      className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary text-foreground/70 hover:text-brand transition"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button 
                      type="button" onClick={() => handleDelete(item.id)}
                      className="h-8 w-8 grid place-items-center rounded-lg hover:bg-destructive/10 text-foreground/70 hover:text-destructive transition"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Hand: Interactive Workform */}
        <div className="rounded-2xl border border-border bg-card shadow-sm p-6 position-sticky top-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand">
              {editingId ? "Mutation Assembly Pipeline" : "Instantiation Control Node"}
            </h3>
            {editingId && (
              <button 
                type="button" onClick={handleClearForm}
                className="text-xs text-muted-foreground hover:text-foreground border border-border px-2 py-1 rounded-md bg-secondary/50"
              >
                Abort Edit
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Project Title *</label>
                <input 
                  type="text" name="title" required value={form.title} onChange={handleTitleChange}
                  className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand"
                  placeholder="e.g. Finrux SaaS Platform"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Slug Matrix Reference *</label>
                <input 
                  type="text" name="slug" required value={form.slug} onChange={handleInputChange}
                  className="w-full text-sm rounded-lg border border-border bg-secondary/30 px-3 py-2 focus:outline-none focus:border-brand font-mono text-xs"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Domain Category *</label>
                <input 
                  type="text" name="category" required value={form.category} onChange={handleInputChange}
                  className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand"
                  placeholder="e.g. Web Architecture, Mobile DevOps"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Client Identity</label>
                <input 
                  type="text" name="client" value={form.client} onChange={handleInputChange}
                  className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand"
                  placeholder="e.g. Indiv Technosoft Corp"
                />
              </div>
            </div>

            {/* NEW EXTENDED DYNAMIC TECH CODES FIELD */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Cpu className="h-3.5 w-3.5 text-brand" /> Core Tech Stack Badges (Separated by commas)
              </label>
              <input 
                type="text" name="technologies" value={form.technologies} onChange={handleInputChange}
                className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand text-foreground"
                placeholder="e.g. React, Tailwind CSS, Node.js, MySQL, TypeScript"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Type entries separated by commas to append individual matrix node tokens.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Image Endpoint Grid URL</label>
                <input 
                  type="url" name="image_url" value={form.image_url} onChange={handleInputChange}
                  className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand text-xs font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">External Live App deployment URL</label>
                <input 
                  type="url" name="project_url" value={form.project_url} onChange={handleInputChange}
                  className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand text-xs font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Short Card Abstract Description</label>
              <textarea 
                name="short_description" rows={2} value={form.short_description} onChange={handleInputChange}
                className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Deep Case Study Narrative Text</label>
              <textarea 
                name="long_description" rows={4} value={form.long_description} onChange={handleInputChange}
                className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-brand font-sans"
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-4 items-center bg-secondary/20 p-4 rounded-xl border border-border/40">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Matrix Order</label>
                <input 
                  type="number" name="display_order" value={form.display_order} onChange={handleInputChange}
                  className="w-20 text-sm rounded-md border border-border bg-background px-2 py-1"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input 
                  type="checkbox" name="is_featured" checked={form.is_featured} onChange={handleInputChange}
                  className="rounded border-border text-brand focus:ring-brand h-4 w-4"
                />
                <span className="text-xs font-medium">Featured</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input 
                  type="checkbox" name="is_active" checked={form.is_active} onChange={handleInputChange}
                  className="rounded border-border text-brand focus:ring-brand h-4 w-4"
                />
                <span className="text-xs font-medium">Publish Active</span>
              </label>
            </div>

            <button
              type="submit" disabled={saving}
              className="w-full bg-brand text-brand-foreground hover:bg-brand/90 transition h-10 rounded-xl font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Commit Changes" : "Assemble Project Instance"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}